"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

import useCountries from "@/app/hooks/useCountries";
import { SafeDiveCenter, SafeListing, SafeReservation, SafeUser } from "@/app/types";

import HeartButton from "../HeartButton";
import Button from "../Button";
import useCurrencies from "@/app/hooks/useCurrencies";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import { ExchangeCurrency } from "@prisma/client";
import RatingStar from "../RatingStar";
import convertPrice from "@/app/utils/CurrencyConverter";
import formatPrice from "@/app/utils/formatPrice";

interface ListingCardProps {
  listing: SafeListing;
  reservation?: SafeReservation;
  onEdit?: (id: string) => void;
  onEditDates?: (id: string) => void;
  onAction?: (id: string) => void;
  disabled?: boolean;
  editing?: boolean;
  editingDates?: boolean;
  actionLabel?: string;
  editLabel?: string;
  editDatesLabel?: string;
  actionId?: string;
  currentUser?: SafeUser | null;
  diveCenter?: SafeDiveCenter | null;
  rates: ExchangeCurrency | null;
}

const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  reservation,
  onEdit,
  onEditDates,
  onAction,
  disabled,
  editing,
  editingDates,
  actionLabel,
  editLabel,
  editDatesLabel,
  actionId = "",
  currentUser,
  diveCenter,
  rates,
}) => {
  const router = useRouter();
  const [convertedPrice, setConvertedPrice] = useState<number | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { getByName } = useCountries();

  const location = getByName(listing.country || "");
  const { getByCode } = useCurrencies();
  const currentCode = getByCode(currentUser?.currency as string);
  const newCurrencySymbol = currentCode?.symbol || '$';
  const currencyStore = useCurrencyStore();
  const selectedCurrency = currencyStore.selectedCurrency;

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (disabled) {
        return;
      }

      onAction?.(actionId);
    },
    [disabled, onAction, actionId]
  );

  const price = useMemo(() => {
    if (reservation) {
      return reservation.totalPrice;
    }

    return listing.price;
  }, [reservation, listing.price]);

  const diveCenterName = useMemo(() => {
    return listing.diveCenter.user.name;
  }, [listing.diveCenter]);

  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (editing) {
        return;
      }

      onEdit?.(actionId);
    },
    [editing, onEdit, actionId]
  );

  const handleEditDates = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (editingDates) {
        return;
      }

      onEditDates?.(actionId);
    },
    [editingDates, onEditDates, actionId]
  );

  useEffect(() => {
    if (!currentUser) {
      setConvertedPrice(price);
    }
  }, [price, currentUser])

  useEffect(() => {
    let baseCurrency = selectedCurrency?.code || currentUser?.currency;

    if (!baseCurrency) {
      baseCurrency = "USD";
    }

    if (baseCurrency === "USD" && listing.currency === "USD") {
      setConvertedPrice(price);
      return;
    }

    const fetchConvertedPrice = async () => {
      const newPrice = await convertPrice(price, listing.currency || "", baseCurrency, rates);
      setConvertedPrice(parseFloat(newPrice.toFixed(0)));
    };

    fetchConvertedPrice();
  }, [rates, currentUser, price, selectedCurrency, listing.currency]);

  const listingData = {
    title: listing?.title,
    price: listing?.price,
    currency: listing?.currency,
    category: listing?.category,
    city: listing?.city,
    country: listing?.country,
  };
  const urlWithParams = `/listings/${listing?.id}?listingData=${encodeURIComponent(
    JSON.stringify(listingData)
  )}`;

  return (
    <div
      onClick={() => router.push(urlWithParams)}
      className="col-span-1 cursor-pointer group"
    >
      <div className="flex flex-col w-full">
        <div
          className="
            aspect-square 
            w-full 
            relative 
            overflow-hidden 
            rounded-xl
            bg-gray-200
          "
        >
          <Image
            src={listing.imageSrc || "/images/no-photo-yet.jpg"}
            alt={listing.title || "Dive listing"}
            loading="lazy"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 20vw"
            fill
            quality={60}
            className={`
              object-cover 
              transition
              ${imageLoaded ? 'opacity-100 group-hover:scale-110' : 'opacity-0'}
            `}
            onLoad={() => setImageLoaded(true)}
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UyZThmMCIvPjwvc3ZnPg=="
          />
          {!currentUser || currentUser.userType === "Diver" ? (
            <div className="absolute top-3 right-3">
              <HeartButton listingId={listing.id} currentUser={currentUser} />
            </div>
          ) : null}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="font-medium text-sm capitalize truncate max-w-[70%]">
            {diveCenterName}
          </div>
          <RatingStar averageRating={listing.diveCenter?.averageRating || 0} reviewsNumber={listing.diveCenter?.totalReviews || 0} />
        </div>
        <div className="font-light capitalize truncate">
          {listing.title}
        </div>
        <div className="font-light text-neutral-500 text-sm">
          {listing.category}
        </div>
        <div className="font-semibold text-sm truncate">
          {listing?.city}, {location?.label}
        </div>
        <div className="flex flex-row items-center gap-1">
          <div className="font-semibold">
            {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}&nbsp;
            {convertedPrice !== null ? formatPrice(convertedPrice.toFixed(0)) : '...'}
            {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
              <span className="font-light text-xs">{currentUser?.currency || selectedCurrency?.code}</span>
            )}
          </div>
          {!reservation && <div className="font-light">/ diver</div>}
        </div>
        <div className="flex flex-col gap-2 mt-2">
          {onEdit && (
            <Button
              small
              label={editLabel || ""}
              onClick={handleEdit}
            />
          )}

          {onEditDates && (
            <Button
              small
              label={editDatesLabel || ""}
              onClick={handleEditDates}
            />
          )}

          {onAction && actionLabel && (
            <Button
              disabled={disabled}
              small
              label={actionLabel}
              onClick={handleCancel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingCard;